import { useEffect, useMemo, useRef, useState } from "react";
import {
	Alert,
	Box,
	Button,
	Chip,
	CircularProgress,
	Divider,
	IconButton,
	LinearProgress,
	Paper,
	Stack,
	Typography,
} from "@mui/material";
import CloudUploadRoundedIcon from "@mui/icons-material/CloudUploadRounded";
import PictureAsPdfRoundedIcon from "@mui/icons-material/PictureAsPdfRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import { pythonVmInstance } from "utils/AxiosInstances/RareboxPythonInstance";

const INK = "#111111";
const MAX_FILE_MB = 2048; // 2 GB per file (server enforces the real limit)
const MAX_PAGES = 50; // max pages per conversion job (server enforces the real limit)

const HOW_IT_WORKS = [
	{
		n: "1",
		title: "Upload PDFs",
		body: `Up to ${MAX_FILE_MB} MB per file and ${MAX_PAGES} pages total per conversion.`,
	},
	{
		n: "2",
		title: "Read page by page",
		body: "Every page is read visually, style by style, with a live progress bar.",
	},
	{
		n: "3",
		title: "Download Excel",
		body: "A grouped spreadsheet with product images and auto-detected attributes.",
	},
];

const formatMb = (bytes) => `${((bytes || 0) / (1024 * 1024)).toFixed(1)} MB`;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Upload a file through the Flask API, which streams it to GCS server-side.
// (Simple server-mediated upload — no signed URLs, no bucket CORS.)
const uploadLineSheetFile = (jobId, up, file, onProgress) => {
	const form = new FormData();
	form.append("file", file);
	form.append("index", String(up.index ?? 0));
	return pythonVmInstance.post(`/reports/line-sheet/jobs/${jobId}/upload`, form, {
		onUploadProgress: (e) => {
			if (e.total) onProgress(e.loaded);
		},
	});
};

const PdfToExcel = () => {
	const [files, setFiles] = useState([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const [done, setDone] = useState(false);
	const [dragging, setDragging] = useState(false);
	const [progress, setProgress] = useState({
		pct: 0,
		label: "",
		detail: "",
		indeterminate: true,
	});
	const inputRef = useRef(null);

	useEffect(() => {
		if (!loading) return undefined;
		const handleBeforeUnload = (e) => {
			e.preventDefault();
			e.returnValue = "";
		};
		window.addEventListener("beforeunload", handleBeforeUnload);
		return () => window.removeEventListener("beforeunload", handleBeforeUnload);
	}, [loading]);

	const totalKb = useMemo(
		() => files.reduce((acc, f) => acc + (f.size || 0), 0),
		[files]
	);

	const acceptFiles = (incoming) => {
		setError("");
		setDone(false);
		const list = Array.from(incoming || []);
		if (!list.length) return;
		const nonPdf = list.filter(
			(f) => !String(f.name || "").toLowerCase().endsWith(".pdf")
		);
		if (nonPdf.length) {
			setError("Only PDF files (.pdf) are allowed.");
			return;
		}
		const tooBig = list.filter((f) => (f.size || 0) > MAX_FILE_MB * 1024 * 1024);
		if (tooBig.length) {
			setError(
				`Each PDF must be under ${MAX_FILE_MB} MB. Too large: ${tooBig
					.map((f) => f.name)
					.join(", ")}`
			);
			return;
		}
		// De-duplicate by name + size, append to any existing selection.
		setFiles((prev) => {
			const seen = new Set(prev.map((f) => `${f.name}:${f.size}`));
			const merged = [...prev];
			list.forEach((f) => {
				const key = `${f.name}:${f.size}`;
				if (!seen.has(key)) {
					seen.add(key);
					merged.push(f);
				}
			});
			return merged;
		});
	};

	const onPickFiles = (e) => {
		acceptFiles(e.target.files);
		if (inputRef.current) inputRef.current.value = "";
	};

	const removeFile = (idx) => {
		setError("");
		setDone(false);
		setFiles((prev) => prev.filter((_, i) => i !== idx));
	};

	const clearFiles = () => {
		setFiles([]);
		setError("");
		setDone(false);
	};

	const onDrop = (e) => {
		e.preventDefault();
		setDragging(false);
		if (loading) return;
		acceptFiles(e.dataTransfer?.files);
	};

	const convert = async () => {
		if (!files.length) {
			setError("Select at least one PDF");
			return;
		}
		setLoading(true);
		setError("");
		setDone(false);
		setProgress({ pct: 0, label: "Preparing…", detail: "", indeterminate: true });

		try {
			// 1. Create the job (via the shared axios instance — base URL, auth & 401 handled there).
			const { data: created } = await pythonVmInstance.post("/reports/line-sheet/jobs", {
				files: files.map((f) => ({
					name: f.name,
					size: f.size,
					content_type: "application/pdf",
				})),
			});
			const jobId = created.job_id;
			const uploads = created.uploads || [];

			// 2. Upload each file directly to storage (raw XHR for upload progress, no auth header).
			const totalBytes = files.reduce((a, f) => a + (f.size || 0), 0) || 1;
			let uploadedBefore = 0;
			for (let i = 0; i < uploads.length; i += 1) {
				const file = files[i];
				const up = uploads[i];
				const startedBytes = uploadedBefore; // stable snapshot for the progress closure
				// Sequential by design so the progress total stays meaningful.
				// eslint-disable-next-line no-await-in-loop
				await uploadLineSheetFile(jobId, up, file, (loaded) => {
					const overall = startedBytes + loaded;
					setProgress({
						pct: Math.min(100, Math.round((overall / totalBytes) * 100)),
						label: "Uploading…",
						detail: `${formatMb(overall)} / ${formatMb(totalBytes)}`,
						indeterminate: false,
					});
				});
				uploadedBefore += file.size || 0;
			}

			// 3. Queue processing.
			setProgress({ pct: 0, label: "Queued…", detail: "", indeterminate: true });
			await pythonVmInstance.post(`/reports/line-sheet/jobs/${jobId}/start`);

			// 4. Poll the Mongo-backed job status until done.
			const result = await new Promise((resolve, reject) => {
				const poll = () => {
					pythonVmInstance
						.get(`/reports/line-sheet/jobs/${jobId}`)
						.then((resp) => {
							const j = resp.data;
							if (j.status === "done") {
								resolve(j);
								return;
							}
							if (j.status === "error") {
								reject(new Error(j.error || j.message || "Conversion failed."));
								return;
							}
							const ppct = j.total
								? Math.min(100, Math.round((j.current / j.total) * 100))
								: 0;
							setProgress({
								pct: ppct,
								label: j.message || (j.status === "queued" ? "Queued…" : "Processing…"),
								detail: j.total ? `${j.current} / ${j.total} pages` : "",
								indeterminate: !j.total,
							});
							sleep(1500).then(poll);
						})
						.catch(reject);
				};
				poll();
			});

			// 5. Download the finished file from the server (streamed from GCS by Flask).
			setProgress({ pct: 100, label: "Downloading…", detail: "", indeterminate: false });
			const fileResp = await pythonVmInstance.get(
				`/reports/line-sheet/jobs/${jobId}/download`,
				{ responseType: "blob" }
			);
			const url = window.URL.createObjectURL(fileResp.data);
			const a = document.createElement("a");
			a.href = url;
			a.download = result.filename || "line_sheet.xlsx";
			document.body.appendChild(a);
			a.click();
			a.remove();
			window.URL.revokeObjectURL(url);
			setDone(true);
		} catch (err) {
			const msg =
				err?.response?.data?.message ||
				err?.response?.data?.error ||
				err?.message ||
				"Conversion failed";
			setError(msg);
		} finally {
			setLoading(false);
		}
	};

	const hasFiles = files.length > 0;

	return (
		<Box sx={{ p: { xs: 1.5, md: 3 }, maxWidth: 920, mx: "auto" }}>
			<Paper
				elevation={0}
				sx={{
					borderRadius: 3,
					overflow: "hidden",
					border: (t) => `1px solid ${t.palette.divider}`,
				}}>
				{/* Branded header */}
				<Box sx={{ bgcolor: INK, color: "#fff", px: { xs: 2.5, md: 3.5 }, py: 3 }}>
					<Typography
						sx={{ letterSpacing: 6, fontWeight: 700, fontSize: 12, opacity: 0.85 }}>
						RAREISM
					</Typography>
					<Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5 }}>
						Line Sheet Convertor
					</Typography>
					<Typography variant="body2" sx={{ opacity: 0.7, mt: 0.75, maxWidth: 640 }}>
						Turn line-sheet PDFs into a clean, image-rich Excel — every style with its
						attributes, grouped by category, with product images embedded.
					</Typography>
					<Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: "wrap", gap: 1 }}>
						{["Auto-detects fields", "Groups by category", "Embeds product images"].map(
							(label) => (
								<Chip
									key={label}
									label={label}
									size="small"
									sx={{
										bgcolor: "rgba(255,255,255,0.12)",
										color: "#fff",
										fontWeight: 500,
										borderRadius: 1,
									}}
								/>
							)
						)}
					</Stack>
				</Box>

				<Box sx={{ p: { xs: 2, md: 3.5 } }}>
					{/* Drag & drop upload zone */}
					<Box
						role="button"
						tabIndex={0}
						onClick={() => !loading && inputRef.current?.click()}
						onKeyDown={(e) => {
							if ((e.key === "Enter" || e.key === " ") && !loading)
								inputRef.current?.click();
						}}
						onDragOver={(e) => {
							e.preventDefault();
							if (!loading) setDragging(true);
						}}
						onDragLeave={() => setDragging(false)}
						onDrop={onDrop}
						sx={{
							cursor: loading ? "not-allowed" : "pointer",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							gap: { xs: 1.5, sm: 2.5 },
							flexWrap: "wrap",
							textAlign: { xs: "center", sm: "left" },
							px: 2.5,
							py: 2.5,
							borderRadius: 2.5,
							border: "2px dashed",
							borderColor: dragging ? INK : "divider",
							bgcolor: dragging ? "rgba(17,17,17,0.04)" : "#fafafa",
							transition: "all .15s ease",
							outline: "none",
							"&:hover": { borderColor: loading ? "divider" : "#9e9e9e", bgcolor: "#f5f5f5" },
						}}>
						<CloudUploadRoundedIcon
							sx={{ fontSize: 36, color: dragging ? INK : "text.disabled" }}
						/>
						<Box>
							<Typography sx={{ fontWeight: 600, lineHeight: 1.3 }}>
								{dragging ? "Drop to add files" : "Drag & drop your line-sheet PDFs"}
							</Typography>
							<Typography variant="caption" color="text.secondary">
								or click anywhere here · multiple PDFs · large files OK
							</Typography>
						</Box>
						<Box
							component="span"
							sx={{
								px: 2,
								py: 0.75,
								borderRadius: 1.5,
								border: "1px solid",
								borderColor: "#bdbdbd",
								fontSize: 13,
								fontWeight: 600,
								color: INK,
								whiteSpace: "nowrap",
							}}>
							Browse files
						</Box>
						<input
							ref={inputRef}
							type="file"
							hidden
							accept="application/pdf"
							multiple
							onChange={onPickFiles}
						/>
					</Box>

					{/* Selected files */}
					{hasFiles && (
						<Box sx={{ mt: 2 }}>
							<Stack
								direction="row"
								alignItems="center"
								justifyContent="space-between"
								sx={{ mb: 1 }}>
								<Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
									{files.length} PDF{files.length > 1 ? "s" : ""} selected
									<Typography component="span" color="text.secondary" sx={{ fontWeight: 400 }}>
										{"  ·  "}
										{formatMb(totalKb)} total
									</Typography>
								</Typography>
								<Button
									size="small"
									color="inherit"
									onClick={clearFiles}
									disabled={loading}
									sx={{ textTransform: "none", color: "text.secondary" }}>
									Clear all
								</Button>
							</Stack>
							<Stack spacing={1}>
								{files.map((f, idx) => (
									<Stack
										key={`${f.name}-${f.size}-${idx}`}
										direction="row"
										alignItems="center"
										spacing={1.5}
										sx={{
											px: 1.5,
											py: 1,
											borderRadius: 2,
											border: (t) => `1px solid ${t.palette.divider}`,
											bgcolor: "#fff",
										}}>
										<PictureAsPdfRoundedIcon sx={{ color: "#c0392b" }} />
										<Box sx={{ minWidth: 0, flex: 1 }}>
											<Typography
												variant="body2"
												noWrap
												title={f.name}
												sx={{ fontWeight: 500 }}>
												{f.name}
											</Typography>
											<Typography variant="caption" color="text.secondary">
												{formatMb(f.size)}
											</Typography>
										</Box>
										<IconButton
											size="small"
											onClick={() => removeFile(idx)}
											disabled={loading}
											aria-label={`Remove ${f.name}`}>
											<CloseRoundedIcon fontSize="small" />
										</IconButton>
									</Stack>
								))}
							</Stack>
						</Box>
					)}

					<Divider sx={{ my: 2.5 }} />

					{/* Convert action */}
					<Button
						fullWidth
						variant="contained"
						onClick={convert}
						disabled={loading || !hasFiles}
						startIcon={
							loading ? (
								<CircularProgress size={18} sx={{ color: "inherit" }} />
							) : (
								<AutoAwesomeRoundedIcon />
							)
						}
						sx={{
							bgcolor: INK,
							textTransform: "none",
							fontWeight: 600,
							py: 1.25,
							fontSize: 15,
							"&:hover": { bgcolor: "#000" },
						}}>
						{loading ? "Working…" : "Convert"}
					</Button>
					<Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
						Reads each page visually for best accuracy — picks the product sketch over the
						model photo and groups styles by category.
					</Typography>

					{/* Live progress (upload %, then per-page processing) */}
					{loading && (
						<Box sx={{ mt: 2 }}>
							<Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
								<Typography variant="caption" color="text.secondary">
									{progress.label || "Working…"}
								</Typography>
								<Typography variant="caption" color="text.secondary">
									{progress.indeterminate ? "" : `${progress.pct}%`}
								</Typography>
							</Stack>
							<LinearProgress
								variant={progress.indeterminate ? "indeterminate" : "determinate"}
								value={progress.pct}
								sx={{
									borderRadius: 1,
									height: 8,
									bgcolor: "rgba(17,17,17,0.08)",
									"& .MuiLinearProgress-bar": { bgcolor: INK },
								}}
							/>
							<Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: "block" }}>
								{progress.detail ? `${progress.detail} · ` : ""}
								processing runs on the server — please keep this tab open.
							</Typography>
						</Box>
					)}

					{!loading && done && (
						<Alert
							icon={<CheckCircleRoundedIcon fontSize="inherit" />}
							severity="success"
							sx={{ mt: 2, borderRadius: 2 }}>
							Done — your Excel has been downloaded.
						</Alert>
					)}

					{!loading && error && (
						<Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
							{error}
						</Alert>
					)}

					<Divider sx={{ my: 3 }} />

					{/* How it works — fills the lower area */}
					<Typography
						sx={{
							fontSize: 11,
							letterSpacing: 2,
							fontWeight: 700,
							color: "text.secondary",
							mb: 2,
						}}>
						HOW IT WORKS
					</Typography>
					<Box
						sx={{
							display: "grid",
							gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
							gap: 2,
						}}>
						{HOW_IT_WORKS.map((step) => (
							<Box
								key={step.n}
								sx={{
									p: 2,
									borderRadius: 2.5,
									border: (t) => `1px solid ${t.palette.divider}`,
									bgcolor: "#fafafa",
								}}>
								<Box
									sx={{
										width: 28,
										height: 28,
										borderRadius: "50%",
										bgcolor: INK,
										color: "#fff",
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										fontSize: 13,
										fontWeight: 700,
										mb: 1,
									}}>
									{step.n}
								</Box>
								<Typography sx={{ fontWeight: 700, fontSize: 14 }}>
									{step.title}
								</Typography>
								<Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5, lineHeight: 1.5 }}>
									{step.body}
								</Typography>
							</Box>
						))}
					</Box>
				</Box>
			</Paper>
		</Box>
	);
};

export default PdfToExcel;
